import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/lib/auth/store';
import { config } from '@/config/env.config';

const API_URL = config.api.baseUrl;

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 0,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const { tokens } = useAuthStore.getState();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    // Add language header for i18n
    config.headers['x-lang'] = 'pt-BR';
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Don't intercept auth endpoints (login, register, refresh)
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/refresh');

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      const { tokens, setAuth, clearAuth } = useAuthStore.getState();

      if (tokens?.refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken: tokens.refreshToken,
          });

          const newTokens = response.data;

          const userResponse = await axios.get(`${API_URL}/auth/profile`, {
            headers: {
              Authorization: `Bearer ${newTokens.accessToken}`,
            },
          });

          setAuth(userResponse.data, newTokens);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          }

          return apiClient(originalRequest);
        } catch {
          clearAuth();
          window.location.href = '/login';
        }
      } else {
        clearAuth();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);
